import gsap from 'gsap';

import { isScreenAtLeast } from '../common/utils/index.js';

const ACTIVE_TAB_SELECTOR = '[role="tab"][aria-selected="true"]';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const getTabs = (tabGroup) => [
    ...tabGroup.querySelectorAll(':scope > .tabs__tablist [role="tab"]'),
];

const getPanels = (tabGroup) => [
    ...tabGroup.querySelectorAll(':scope > .tabs__tablist [role="tabpanel"]'),
];

const prefersReducedMotion = () => (
    window.matchMedia(REDUCED_MOTION_QUERY).matches
);

const isDesktopLayout = () => isScreenAtLeast('lg');

const clearPanelProps = (panel) => {
    gsap.set(panel, {
        clearProps: 'height,opacity,overflow,transform',
    });
};

const setActiveAttributes = (tabs, activeIndex, shouldFocus) => {
    tabs.forEach((tab, index) => {
        const isActive = index === activeIndex;

        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');

        if (isActive && shouldFocus) {
            tab.focus();
        }
    });
};

const showPanelInstantly = (panel) => {
    panel.hidden = false;
    clearPanelProps(panel);
};

const hidePanelInstantly = (panel) => {
    panel.hidden = true;
    clearPanelProps(panel);
};

const normalizeInactivePanels = (panels, activeIndex, previousIndex) => {
    panels.forEach((panel, index) => {
        if (index === activeIndex || index === previousIndex) {
            return;
        }

        hidePanelInstantly(panel);
    });
};

const animateDesktopTransition = (panels, previousIndex, activeIndex) => {
    const outgoingPanel = panels[previousIndex];
    const incomingPanel = panels[activeIndex];

    if (!incomingPanel) {
        return;
    }

    const timeline = gsap.timeline();

    if (outgoingPanel && outgoingPanel !== incomingPanel && !outgoingPanel.hidden) {
        timeline.to(outgoingPanel, {
            duration: 0.18,
            ease: 'power1.out',
            opacity: 0,
            y: -6,
            onComplete: () => hidePanelInstantly(outgoingPanel),
        });
    }

    timeline.add(() => {
        incomingPanel.hidden = false;
        gsap.set(incomingPanel, {
            opacity: 0,
            y: 8,
        });
    });

    timeline.to(incomingPanel, {
        duration: 0.24,
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        onComplete: () => clearPanelProps(incomingPanel),
    });
};

const animateMobileTransition = (panels, previousIndex, activeIndex) => {
    const outgoingPanel = panels[previousIndex];
    const incomingPanel = panels[activeIndex];

    if (!incomingPanel) {
        return;
    }

    if (outgoingPanel && outgoingPanel !== incomingPanel && !outgoingPanel.hidden) {
        gsap.set(outgoingPanel, {
            height: outgoingPanel.offsetHeight,
            overflow: 'hidden',
        });

        gsap.to(outgoingPanel, {
            duration: 0.28,
            ease: 'power2.inOut',
            height: 0,
            opacity: 0,
            y: -8,
            onComplete: () => hidePanelInstantly(outgoingPanel),
        });
    }

    incomingPanel.hidden = false;
    gsap.set(incomingPanel, {
        height: 'auto',
        overflow: 'hidden',
    });

    const targetHeight = incomingPanel.offsetHeight;

    gsap.fromTo(
        incomingPanel,
        {
            height: 0,
            opacity: 0,
            y: 8,
        },
        {
            duration: 0.32,
            ease: 'power2.out',
            height: targetHeight,
            opacity: 1,
            y: 0,
            onComplete: () => clearPanelProps(incomingPanel),
        }
    );
};

const activateTab = (tabGroup, activeIndex, shouldFocus = false) => {
    const tabs = getTabs(tabGroup);
    const panels = getPanels(tabGroup);
    const previousIndex = tabs.findIndex((tab) => tab.matches(ACTIVE_TAB_SELECTOR));

    if (previousIndex === activeIndex) {
        setActiveAttributes(tabs, activeIndex, shouldFocus);
        showPanelInstantly(panels[activeIndex]);
        return;
    }

    gsap.killTweensOf(panels);
    setActiveAttributes(tabs, activeIndex, shouldFocus);
    normalizeInactivePanels(panels, activeIndex, previousIndex);

    if (prefersReducedMotion()) {
        panels.forEach((panel, index) => {
            if (index === activeIndex) {
                showPanelInstantly(panel);
                return;
            }

            hidePanelInstantly(panel);
        });
        return;
    }

    if (isDesktopLayout()) {
        animateDesktopTransition(panels, previousIndex, activeIndex);
        return;
    }

    animateMobileTransition(panels, previousIndex, activeIndex);
};

const getNextIndex = (currentIndex, length, direction) => {
    if (direction === 'previous') {
        return currentIndex === 0 ? length - 1 : currentIndex - 1;
    }

    return currentIndex === length - 1 ? 0 : currentIndex + 1;
};

const initializeTabs = (tabGroup) => {
    const tabs = getTabs(tabGroup);

    if (tabs.length === 0) {
        return;
    }

    tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
            activateTab(tabGroup, index);
        });

        tab.addEventListener('keydown', (event) => {
            const currentIndex = tabs.indexOf(tab);
            let nextIndex = null;

            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                nextIndex = getNextIndex(currentIndex, tabs.length, 'previous');
            }

            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                nextIndex = getNextIndex(currentIndex, tabs.length, 'next');
            }

            if (event.key === 'Home') {
                nextIndex = 0;
            }

            if (event.key === 'End') {
                nextIndex = tabs.length - 1;
            }

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                activateTab(tabGroup, currentIndex);
                return;
            }

            if (nextIndex !== null) {
                event.preventDefault();
                activateTab(tabGroup, nextIndex, true);
            }
        });
    });
};

const initialize = () => {
    document.querySelectorAll('.tabs').forEach(initializeTabs);
};

export default {
    initialize,
};
